// EditNewsPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import CreateNewsPage from './CreateNewsPage';

const EditNewsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useSelector(state => state.profile);
  const newsRoles = useSelector((state) => state?.roles?.data?.role?.news);
  const newsAdmin = [0, 1].includes(profile.profileLevel) || newsRoles?.all;

  const [newsData, setNewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  // Check if user is authorized to edit this news article
  useEffect(() => {
    const checkAuthorization = async () => {
      if (!id) {
        toast.error('Invalid news article ID');
        navigate('/home/news');
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/news/${id}`);
        const result = await response.json();

        if (result.success) {
          const article = result.news;

          // Check if user is authorized (admin or owner)
          const isOwner = article.authorId === profile._id;
          const isAdmin = newsAdmin;

          if (isOwner || isAdmin) {
            setNewsData(article);
            setAuthorized(true);
          } else {
            toast.error('You are not authorized to edit this article');
            navigate(`/home/news/${id}`);
            return;
          }
        } else {
          toast.error('Article not found');
          navigate('/home/news');
          return;
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        toast.error('Failed to load article');
        navigate('/home/news');
        return;
      } finally {
        setLoading(false);
      }
    };

    checkAuthorization();
  }, [id, profile._id, newsAdmin, navigate]);

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A3A4C] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!authorized || !newsData) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Access Denied</p>
          <button
            onClick={() => navigate('/home/news')}
            className="dynamic-site-bg text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity duration-200"
          >
            Back to News
          </button>
        </div>
      </div>
    );
  }

  // Pass the existing news data to CreateNewsPage as edit mode
  return <CreateNewsPage editMode={true} existingNews={newsData} />;
};

export default EditNewsPage;
